import React, { useEffect, useMemo, useState } from 'react'
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined'
import RestoreIcon from '@mui/icons-material/Restore'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { createManagedUser } from '../utils/createManagedUser'

const emptyCreateForm = {
  email: '',
  password: '',
  nickname: '',
  firstName: '',
  lastName: '',
  mobilePhone: '',
  role: 'user',
  department: 'CMI',
}

const departments = ['CMI', 'IT', 'HR']

const normalizeUser = (snapshot) => {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    ...data,
    role: data.role === 'admin' ? 'admin' : 'user',
    status: data.status === 'passive' ? 'passive' : 'active',
    department: departments.includes(data.department) ? data.department : '',
    archived: data.archived === true,
  }
}

const timestampValue = (value) => {
  if (value?.toMillis) return value.toMillis()
  if (value instanceof Date) return value.getTime()
  return 0
}

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`users-tabpanel-${index}`}
      aria-labelledby={`users-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  )
}

function SummaryCard({ icon, label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: color }}>{icon}</Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </Box>
    </Paper>
  )
}

export default function AdminUsersPage() {
  const { t } = useTranslation()
  const { currentUser, userRole } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [tabValue, setTabValue] = useState(0)
  const [updatingUserId, setUpdatingUserId] = useState(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createForm, setCreateForm] = useState(emptyCreateForm)
  const [creatingUser, setCreatingUser] = useState(false)
  const [archiveCandidate, setArchiveCandidate] = useState(null)
  const [editingUser, setEditingUser] = useState(null)
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', mobilePhone: '' })
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  useEffect(() => {
    if (userRole !== 'admin') return undefined

    const unsubscribe = onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        const userList = snapshot.docs
          .map(normalizeUser)
          .sort((a, b) => timestampValue(b.createdAt) - timestampValue(a.createdAt))

        setUsers(userList)
        setLoadError(false)
        setLoading(false)
      },
      (error) => {
        console.error('Kullanıcılar alınamadı:', error)
        setLoadError(true)
        setLoading(false)
      },
    )

    return unsubscribe
  }, [userRole])

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity })
  }

  const adminCount = users.filter((user) => !user.archived && user.role === 'admin').length

  const handleCreateUser = async (event) => {
    event.preventDefault()
    setCreatingUser(true)
    try {
      await createManagedUser({
        ...createForm,
        createdBy: currentUser.uid,
      })
      setCreateForm(emptyCreateForm)
      setCreateDialogOpen(false)
      showMessage(t('adminUsers.userCreated'))
    } catch (error) {
      console.error('Kullanıcı oluşturma hatası:', error)
      const message = error?.code === 'auth/email-already-in-use'
        ? t('adminUsers.emailInUse')
        : t('adminUsers.userCreateError')
      showMessage(message, 'error')
    } finally {
      setCreatingUser(false)
    }
  }

  const handleArchiveUser = async () => {
    const user = archiveCandidate
    if (!user) return
    setUpdatingUserId(user.id)
    try {
      await updateDoc(doc(db, 'users', user.id), {
        archived: true,
        status: 'passive',
        archivedAt: serverTimestamp(),
        archivedBy: currentUser.uid,
      })
      setArchiveCandidate(null)
      showMessage(t('adminUsers.userArchived'))
    } catch (error) {
      console.error('Kullanıcı çıkarma hatası:', error)
      showMessage(t('adminUsers.userArchiveError'), 'error')
    } finally {
      setUpdatingUserId(null)
    }
  }

  const handleRestoreUser = async (user) => {
    setUpdatingUserId(user.id)
    try {
      await updateDoc(doc(db, 'users', user.id), {
        archived: false,
        status: 'active',
        restoredAt: serverTimestamp(),
        restoredBy: currentUser.uid,
      })
      showMessage(t('adminUsers.userRestored'))
    } catch (error) {
      console.error('Kullanıcı geri alma hatası:', error)
      showMessage(t('adminUsers.userRestoreError'), 'error')
    } finally {
      setUpdatingUserId(null)
    }
  }

  const openEditDialog = (user) => {
    setEditingUser(user)
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      mobilePhone: user.mobilePhone || '',
    })
  }

  const handleEditUser = async (event) => {
    event.preventDefault()
    if (!editingUser) return

    setUpdatingUserId(editingUser.id)
    try {
      await updateDoc(doc(db, 'users', editingUser.id), {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        mobilePhone: editForm.mobilePhone.trim(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      })
      setEditingUser(null)
      showMessage(t('adminUsers.userInfoUpdated'))
    } catch (error) {
      console.error('Kullanıcı bilgileri güncelleme hatası:', error)
      showMessage(t('adminUsers.userInfoError'), 'error')
    } finally {
      setUpdatingUserId(null)
    }
  }

  const handleRoleChange = async (user, newRole) => {
    if (user.role === newRole) return
    if (user.id === currentUser?.uid) {
      showMessage(t('adminUsers.selfUpdateWarning'), 'warning')
      return
    }
    if (user.role === 'admin' && newRole === 'user' && adminCount <= 1) {
      showMessage(t('adminUsers.adminWarning'), 'error')
      return
    }

    setUpdatingUserId(user.id)
    try {
      await updateDoc(doc(db, 'users', user.id), {
        role: newRole,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      })
      showMessage(t('adminUsers.userRoleUpdated'))
    } catch (error) {
      console.error('Rol güncelleme hatası:', error)
      showMessage(t('adminUsers.userRoleError'), 'error')
    } finally {
      setUpdatingUserId(null)
    }
  }

  const handleStatusChange = async (user, newStatus) => {
    if (user.status === newStatus) return
    if (user.id === currentUser?.uid) {
      showMessage(t('adminUsers.selfUpdateWarning'), 'warning')
      return
    }

    setUpdatingUserId(user.id)
    try {
      await updateDoc(doc(db, 'users', user.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      })
      showMessage(t('adminUsers.statusUpdated'))
    } catch (error) {
      console.error('Durum güncelleme hatası:', error)
      showMessage(t('adminUsers.statusError'), 'error')
    } finally {
      setUpdatingUserId(null)
    }
  }

  const handleDepartmentChange = async (user, newDepartment) => {
    if (user.department === newDepartment) return
    setUpdatingUserId(user.id)
    try {
      await updateDoc(doc(db, 'users', user.id), {
        department: newDepartment,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      })
      showMessage(t('adminUsers.departmentUpdated'))
    } catch (error) {
      console.error('Departman güncelleme hatası:', error)
      showMessage(t('adminUsers.departmentError'), 'error')
    } finally {
      setUpdatingUserId(null)
    }
  }

  const filteredUsers = useMemo(() => {
    const search = searchTerm.trim().toLocaleLowerCase()
    if (!search) return users

    return users.filter((user) => (
      [user.email, user.nickname, user.firstName, user.lastName, user.mobilePhone, user.department]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(search))
    ))
  }, [searchTerm, users])

  const activeUsers = filteredUsers.filter((user) => !user.archived && user.status === 'active')
  const passiveUsers = filteredUsers.filter((user) => !user.archived && user.status === 'passive')
  const archivedUsers = filteredUsers.filter((user) => user.archived)

  const renderUsers = (items) => {
    if (!items.length) {
      return (
        <Alert severity="info">
          {searchTerm ? t('adminUsers.noSearchResults') : t('adminUsers.noUsers')}
        </Alert>
      )
    }

    return (
      <List disablePadding>
        {items.map((user) => {
          const isCurrentUser = user.id === currentUser?.uid
          const isUpdating = updatingUserId === user.id
          const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ')
          const avatarLetter = (user.nickname || displayName || user.email || '?')[0].toUpperCase()

          return (
            <ListItem
              key={user.id}
              divider
              sx={{
                gap: 2,
                py: 2,
                alignItems: { xs: 'stretch', md: 'center' },
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <ListItemAvatar>
                  <Avatar src={user.avatarUrl || ''}>{avatarLetter}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography component="span" fontWeight={600}>{user.email}</Typography>
                      {isCurrentUser && <Chip size="small" label={t('adminUsers.you')} color="primary" />}
                    </Box>
                  }
                  secondary={
                    [
                      user.nickname && `${t('adminUsers.nickname')}: ${user.nickname}`,
                      displayName && `${t('adminUsers.fullName')}: ${displayName}`,
                      user.mobilePhone && `${t('adminUsers.phoneLabel')}: ${user.mobilePhone}`,
                    ].filter(Boolean).join(' • ') || t('adminUsers.profileIncomplete')
                  }
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {user.archived ? (
                  <Button
                    variant="outlined"
                    startIcon={<RestoreIcon />}
                    disabled={isUpdating}
                    onClick={() => handleRestoreUser(user)}
                  >
                    {t('adminUsers.restore')}
                  </Button>
                ) : (
                  <>
                <Tooltip title={t('adminUsers.editUser')}>
                  <span>
                    <IconButton
                      color="primary"
                      disabled={isUpdating}
                      onClick={() => openEditDialog(user)}
                      aria-label={t('adminUsers.editUser')}
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <FormControl size="small" sx={{ minWidth: 130, flex: { xs: 1, sm: 'none' } }}>
                  <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
                  <Select
                    label={t('adminUsers.roleLabel')}
                    value={user.role}
                    disabled={isCurrentUser || isUpdating}
                    onChange={(event) => handleRoleChange(user, event.target.value)}
                  >
                    <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                    <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130, flex: { xs: 1, sm: 'none' } }}>
                  <InputLabel>{t('adminUsers.statusLabel')}</InputLabel>
                  <Select
                    label={t('adminUsers.statusLabel')}
                    value={user.status}
                    disabled={isCurrentUser || isUpdating}
                    onChange={(event) => handleStatusChange(user, event.target.value)}
                  >
                    <MenuItem value="active">{t('adminUsers.active')}</MenuItem>
                    <MenuItem value="passive">{t('adminUsers.passive')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130, flex: { xs: 1, sm: 'none' } }}>
                  <InputLabel>{t('adminUsers.departmentLabel')}</InputLabel>
                  <Select
                    label={t('adminUsers.departmentLabel')}
                    value={user.department}
                    disabled={isUpdating}
                    onChange={(event) => handleDepartmentChange(user, event.target.value)}
                  >
                    {!user.department && (
                      <MenuItem value="" disabled>{t('adminUsers.departmentNotSelected')}</MenuItem>
                    )}
                    {departments.map((department) => (
                      <MenuItem key={department} value={department}>{department}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                  <Tooltip title={t('adminUsers.archive')}>
                    <span>
                      <IconButton
                        color="error"
                        disabled={isCurrentUser || isUpdating}
                        onClick={() => setArchiveCandidate(user)}
                        aria-label={t('adminUsers.archive')}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  </>
                )}
                {isUpdating && <CircularProgress size={22} aria-label={t('adminUsers.updating')} />}
              </Box>
            </ListItem>
          )
        })}
      </List>
    )
  }

  if (userRole !== 'admin') {
    return <Container sx={{ mt: 4 }}><Alert severity="error">{t('adminUsers.noAccess')}</Alert></Container>
  }

  if (loading) {
    return (
      <Container sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label={t('adminUsers.loading')} />
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleAltIcon color="primary" />
            {t('adminUsers.pageTitle')}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>{t('adminUsers.pageDescription')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
          {t('adminUsers.addUser')}
        </Button>
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 3 }}>{t('adminUsers.loadError')}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard icon={<PeopleAltIcon />} label={t('adminUsers.totalUsers')} value={users.filter((user) => !user.archived).length} color="primary.main" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard icon={<CheckCircleOutlineIcon />} label={t('adminUsers.activeTab')} value={users.filter((user) => !user.archived && user.status === 'active').length} color="success.main" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard icon={<AdminPanelSettingsOutlinedIcon />} label={t('adminUsers.admins')} value={adminCount} color="warning.main" />
        </Grid>
      </Grid>

      <Card variant="outlined">
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            label={t('adminUsers.searchLabel')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            fullWidth
            inputProps={{ 'aria-label': t('adminUsers.searchLabel') }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="fullWidth"
          aria-label={t('adminUsers.pageTitle')}
        >
          <Tab id="users-tab-0" icon={<CheckCircleOutlineIcon />} iconPosition="start" label={`${t('adminUsers.activeTab')} (${activeUsers.length})`} />
          <Tab id="users-tab-1" icon={<PersonOffOutlinedIcon />} iconPosition="start" label={`${t('adminUsers.passiveTab')} (${passiveUsers.length})`} />
          <Tab id="users-tab-2" icon={<DeleteOutlineIcon />} iconPosition="start" label={`${t('adminUsers.archivedTab')} (${archivedUsers.length})`} />
        </Tabs>

        <Box sx={{ px: 2, pb: 2 }}>
          <TabPanel value={tabValue} index={0}>{renderUsers(activeUsers)}</TabPanel>
          <TabPanel value={tabValue} index={1}>{renderUsers(passiveUsers)}</TabPanel>
          <TabPanel value={tabValue} index={2}>{renderUsers(archivedUsers)}</TabPanel>
        </Box>
      </Card>

      <Dialog open={createDialogOpen} onClose={() => !creatingUser && setCreateDialogOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleCreateUser}>
          <DialogTitle>{t('adminUsers.addUser')}</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
            <TextField
              required
              type="email"
              label={t('adminUsers.emailLabel')}
              value={createForm.email}
              onChange={(event) => setCreateForm((value) => ({ ...value, email: event.target.value }))}
              autoComplete="off"
            />
            <TextField
              required
              label={t('adminUsers.nickname')}
              value={createForm.nickname}
              onChange={(event) => setCreateForm((value) => ({ ...value, nickname: event.target.value }))}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label={t('adminUsers.firstNameLabel')}
                  value={createForm.firstName}
                  onChange={(event) => setCreateForm((value) => ({ ...value, firstName: event.target.value }))}
                  autoComplete="given-name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label={t('adminUsers.lastNameLabel')}
                  value={createForm.lastName}
                  onChange={(event) => setCreateForm((value) => ({ ...value, lastName: event.target.value }))}
                  autoComplete="family-name"
                />
              </Grid>
            </Grid>
            <TextField
              required
              type="tel"
              label={t('adminUsers.phoneLabel')}
              value={createForm.mobilePhone}
              onChange={(event) => setCreateForm((value) => ({ ...value, mobilePhone: event.target.value }))}
              placeholder={t('adminUsers.phonePlaceholder')}
              inputProps={{ inputMode: 'tel', minLength: 7, maxLength: 20 }}
              autoComplete="tel"
            />
            <TextField
              required
              type="password"
              label={t('adminUsers.temporaryPassword')}
              value={createForm.password}
              onChange={(event) => setCreateForm((value) => ({ ...value, password: event.target.value }))}
              inputProps={{ minLength: 6 }}
              helperText={t('adminUsers.passwordHelp')}
              autoComplete="new-password"
            />
            <FormControl>
              <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
              <Select
                label={t('adminUsers.roleLabel')}
                value={createForm.role}
                onChange={(event) => setCreateForm((value) => ({ ...value, role: event.target.value }))}
              >
                <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl required>
              <InputLabel>{t('adminUsers.departmentLabel')}</InputLabel>
              <Select
                label={t('adminUsers.departmentLabel')}
                value={createForm.department}
                onChange={(event) => setCreateForm((value) => ({ ...value, department: event.target.value }))}
              >
                {departments.map((department) => (
                  <MenuItem key={department} value={department}>{department}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)} disabled={creatingUser}>{t('adminUsers.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={creatingUser}>
              {creatingUser ? <CircularProgress size={22} /> : t('adminUsers.create')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(archiveCandidate)} onClose={() => setArchiveCandidate(null)}>
        <DialogTitle>{t('adminUsers.archiveTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('adminUsers.archiveConfirm', { email: archiveCandidate?.email })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveCandidate(null)}>{t('adminUsers.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleArchiveUser}>{t('adminUsers.archive')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingUser)}
        onClose={() => updatingUserId !== editingUser?.id && setEditingUser(null)}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleEditUser}>
          <DialogTitle>{t('adminUsers.editUser')}</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
            <Typography color="text.secondary">{editingUser?.email}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label={t('adminUsers.firstNameLabel')}
                  value={editForm.firstName}
                  onChange={(event) => setEditForm((value) => ({ ...value, firstName: event.target.value }))}
                  autoComplete="given-name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label={t('adminUsers.lastNameLabel')}
                  value={editForm.lastName}
                  onChange={(event) => setEditForm((value) => ({ ...value, lastName: event.target.value }))}
                  autoComplete="family-name"
                />
              </Grid>
            </Grid>
            <TextField
              required
              type="tel"
              label={t('adminUsers.phoneLabel')}
              value={editForm.mobilePhone}
              onChange={(event) => setEditForm((value) => ({ ...value, mobilePhone: event.target.value }))}
              placeholder={t('adminUsers.phonePlaceholder')}
              inputProps={{ inputMode: 'tel', minLength: 7, maxLength: 20 }}
              autoComplete="tel"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingUser(null)} disabled={updatingUserId === editingUser?.id}>
              {t('adminUsers.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={updatingUserId === editingUser?.id}>
              {updatingUserId === editingUser?.id ? <CircularProgress size={22} /> : t('adminUsers.save')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((value) => ({ ...value, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((value) => ({ ...value, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
